import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Heading } from '@frontend/design-system';
import { AvailabilityDateNavigator } from '../../BookingSite/RequestAppointment/ProviderSelect/availability-date-navigator';
import type { AvailabilityHeaderFieldProps } from './availability-header.types';
import { AvailabilityRequestSourceDropdown } from './availability-request-source-dropdown';
import { AvailabilityServiceSelector } from './availability-service-selector';
import { FilterTrayTrigger } from './filter-tray-trigger';
import type { FilterConfig } from './filters-tray';
import { InlineFilterRow, useInlineFilterRow } from './inline-filter-row';

type AvailabilityHeaderProps = AvailabilityHeaderFieldProps & {
  locationId: string;
  orgId: string;
};

/** CSS container name applied to the header's root element. */
const AVAILABILITY_HEADER_CONTAINER_NAME = 'availability-header';

/** Container widths */
const headerBreakpoints = {
  dateSelector: 540,
  serviceSelector: breakpoints.small.max,
  locationSelector: 731,
  timeOfDaySelector: 745,
  filterButton1: 390,
  filterButton2: 540,
};

export const AvailabilityHeader = ({
  locationId,
  orgId,
  weekInView,
  weekDatePicker,
  handleDateChange,
  goToNextWeek,
  goToPreviousWeek,
  goToThisWeek,
  isCurrentWeek,
  selectedServiceId,
  onServiceSelect,
  isMultiLocation,
  locationOptions,
  locationRadioField,
  timeRangeOptions,
  timeRangeChecklistField,
  operatories,
  operatoryChecklistField,
  providers,
  providerChecklistField,
  requestSourceField,
}: AvailabilityHeaderProps) => {
  const { t } = useTranslation('schedule');

  const { containerRef, containerWidth } = useInlineFilterRow();

  // `minInlineWidth` declares the container width (px) above which each filter
  // is shown inline on the row; below it the filter lives in the tray.
  const hasOperatories = operatories.length > 0;
  const filters: FilterConfig[] = [
    {
      key: 'locationId',
      type: 'radio',
      title: t('Location'),
      iconName: 'location-small',
      items: isMultiLocation && locationOptions.length > 1 ? locationOptions : [],
      fieldProps: locationRadioField,
      resetValue: locationOptions[0]?.value ?? '',
      minInlineWidth: headerBreakpoints.locationSelector,
    },
    {
      key: 'providerIds',
      type: 'checklist',
      title: t('Provider'),
      iconName: 'user-small',
      items: providers,
      fieldProps: providerChecklistField,
      minInlineWidth: headerBreakpoints.timeOfDaySelector,
    },
    {
      key: 'operatoryIds',
      type: 'checklist',
      title: t('Operatory'),
      iconName: 'department-small',
      items: operatories,
      fieldProps: operatoryChecklistField,
      minInlineWidth: headerBreakpoints.timeOfDaySelector,
    },
    {
      key: 'timeRanges',
      type: 'checklist',
      title: t('Time of Day'),
      iconName: 'clock-small',
      items: timeRangeOptions,
      fieldProps: timeRangeChecklistField,
      hideSearch: true,
      minInlineWidth: hasOperatories ? headerBreakpoints.timeOfDaySelector + 150 : headerBreakpoints.timeOfDaySelector,
    },
  ];

  // Eligible = has at least one item to show.
  // Passed to both the inline pill row and FilterTrayTrigger.
  const eligibleFilters = filters.filter((f) => f.items.length > 0);

  const serviceSelector = (
    <AvailabilityServiceSelector
      locationId={locationId}
      orgId={orgId}
      selectedId={selectedServiceId}
      onSelect={onServiceSelect}
    />
  );

  const filterTrayButton = (
    <FilterTrayTrigger
      filters={filters}
      containerWidth={containerWidth}
      containerName={AVAILABILITY_HEADER_CONTAINER_NAME}
    />
  );
  const dateSelector = (
    <section css={styles.dateSelector}>
      {/*
       * Today button: CSS container query hides the text label and shows the
       * icon-only variant below SERVICE_SELECTOR_BREAKPOINT, and vice-versa above it.
       */}
      <Button variant='secondary' size='small' disabled={isCurrentWeek} onClick={goToThisWeek}>
        {t('Today')}
      </Button>
      <Button
        variant='secondary'
        size='small'
        iconName='caret-left'
        disabled={isCurrentWeek}
        onClick={goToPreviousWeek}
      />
      <AvailabilityDateNavigator
        key={weekInView}
        weekInView={weekInView}
        minDate={dayjs().format('MM/DD/YYYY')}
        handleDateChange={handleDateChange}
        dateFieldProps={weekDatePicker}
        isSchedularV3Enabled={true}
        selectedLocationTimeZone=''
      />
      <Button variant='secondary' size='small' iconName='caret-right' onClick={goToNextWeek} />
      <div css={styles.filterTrayButtonOnRow1}>{filterTrayButton}</div>
    </section>
  );

  return (
    <section css={styles.container} ref={containerRef}>
      {/* ── Row 1: Heading · Date navigation · Service selector ── */}
      <section css={styles.topRow}>
        <Heading level={3} css={styles.heading}>
          {t('Availability Finder')}
        </Heading>
        <div css={[styles.dateSelectorOnRow1]}>{dateSelector}</div>

        {/*
         * Service selector on Row 1 — hidden below SERVICE_SELECTOR_BREAKPOINT via container query.
         * A second copy lives on Row 2 (visible only below SERVICE_SELECTOR_BREAKPOINT).
         */}
        <div css={[styles.serviceTypeContainer, styles.serviceTypeOnRow1]}>{serviceSelector}</div>
      </section>

      <section css={styles.bottomRow}>
        <div css={styles.dateSelectorOnRow2}>{dateSelector}</div>
        <div css={styles.serviceTypeOnRow2}>{serviceSelector}</div>
        {requestSourceField && <AvailabilityRequestSourceDropdown field={requestSourceField} />}
        <InlineFilterRow filters={eligibleFilters} containerName={AVAILABILITY_HEADER_CONTAINER_NAME} />
        <div css={styles.filterTrayButtonOnRow2}>{filterTrayButton}</div>
      </section>
    </section>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.neutral20};
    border-bottom: 1px solid ${theme.colors.neutral20};
    width: 100%;
    container-type: inline-size;
    container-name: ${AVAILABILITY_HEADER_CONTAINER_NAME};
  `,
  topRow: css`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1.5)};
    padding-bottom: var(--fabric-space-gap-sm);
    border-bottom: 1px solid var(--fabric-color-border-subtle);
  `,
  bottomRow: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--fabric-space-gap-md);
    padding: ${theme.spacing(1.5)};
    padding-top: ${theme.spacing(1)};
    justify-content: flex-end;
  `,
  heading: css`
    margin: 0;
    margin-right: ${theme.spacing(1)};
    white-space: nowrap;
    flex-shrink: 0;
  `,
  serviceTypeContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    margin-left: auto;
    flex-shrink: 0;
  `,
  serviceTypeOnRow1: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (max-width: ${headerBreakpoints.serviceSelector}px) {
      display: none;
    }
  `,
  serviceTypeOnRow2: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (min-width: ${headerBreakpoints.serviceSelector + 1}px) {
      display: none;
    }
  `,
  dateSelectorOnRow1: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (max-width: ${headerBreakpoints.dateSelector}px) {
      display: none;
    }
  `,
  dateSelectorOnRow2: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (min-width: ${headerBreakpoints.dateSelector + 1}px) {
      display: none;
    }
  `,
  filterTrayButtonOnRow1: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (max-width: ${headerBreakpoints.filterButton1}px) or (min-width: ${headerBreakpoints.filterButton2 +
    1}px) {
      display: none;
    }
  `,
  filterTrayButtonOnRow2: css`
    @container ${AVAILABILITY_HEADER_CONTAINER_NAME} (${headerBreakpoints.filterButton1 +
    1}px <= width <= ${headerBreakpoints.filterButton2}px) {
      display: none;
    }
  `,
  dateSelector: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    flex-shrink: 0;
  `,
};
